import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import SideBarModal from '../modal/SideBarModal';
import { LoadedUser, Section } from '../../interfaces';
import { getClientSection } from '../../utils/modal/paymentModal';
import {
  getPaymentLineItems,
  isCardPaymentMethod,
  isCashDiscountEnabled,
  isPaymentComplete,
} from '../../helpers/payments';
import PaymentLineItem from '../modal/PaymentLineItem';
import useFeatureGuard from '../../hooks/subscriptions/useFeatureGuard';
import { Feature } from '../../types/subscriptions';
import useUser from '../../hooks/global/useUser';
import useCancelCheckout from '../../hooks/payments/useCancelCheckout';
import useNavigateToCustomerProfile from '../../hooks/customers/useNavigateToCustomerProfile';
import { PaymentsGroupDetail } from '../../types/payments';
import useGetPaymentPaymentsGroup from '../../hooks/payments/useGetPaymentPaymentsGroup';
import { CREDIT_PAYMENT_METHODS } from '../../constants/payments';
import { getAllUserServices } from '../../helpers/services';

const isPendingCredit = (group: PaymentsGroupDetail) =>
  group.payments.some(
    p =>
      isCardPaymentMethod(p.method) &&
      (p.status === 'PENDING' || p.status === 'FAILED'),
  ) &&
  (group?.order?.checkoutSessionActive ||
    group?.appointment?.checkoutSessionActive);

type Props = {
  paymentId: string | null;
  onClose: () => void;
};

function PaymentsSideBarModal({ paymentId, onClose }: Props) {
  const { user } = useUser<LoadedUser>();
  const canCheckout = useFeatureGuard(Feature.CHECKOUT);
  const terminalReaderId = user?.business?.terminalReaderId;

  const { data: paymentsGroup, refetch } =
    useGetPaymentPaymentsGroup(paymentId);

  const refresh = React.useCallback(async () => {
    await refetch();
  }, [refetch]);

  const { mutateAsync: cancelCheckout, isPending: isLoadingCancel } =
    useCancelCheckout({
      onSuccess: refresh,
    });

  const navigateToCustomer = useNavigateToCustomerProfile();

  const onCancelCheckout = React.useCallback(async () => {
    if (!paymentsGroup || isLoadingCancel) return;

    const isPending = isPendingCredit(paymentsGroup);
    if (!isPending) return;

    const paymentMethod =
      paymentsGroup.order?.paymentMethod ??
      paymentsGroup.appointment?.paymentMethod;

    const readerId =
      paymentMethod === 'TERMINAL' ? terminalReaderId : undefined;

    try {
      const response = await cancelCheckout({
        appointmentId: paymentsGroup.appointment?.appointmentId,
        orderId: paymentsGroup.order?.orderId,
        terminalReaderId: readerId,
      });

      if (response) {
        const successMsg =
          paymentMethod === 'TERMINAL'
            ? 'Terminal checkout canceled.'
            : 'Checkout canceled.';
        toast.success(successMsg);
      }
    } catch (cancelError) {
      const errorMsg =
        paymentMethod === 'TERMINAL'
          ? 'Error canceling terminal checkout'
          : 'Error canceling checkout.';
      toast.error(errorMsg);
    }
  }, [paymentsGroup, isLoadingCancel, cancelCheckout, terminalReaderId]);

  const sections = React.useMemo(() => {
    const tempSections: Section[] = [];
    if (!paymentsGroup) return tempSections;

    const isPending = isPendingCredit(paymentsGroup);
    const pendingMethod =
      paymentsGroup?.order?.paymentMethod ??
      paymentsGroup?.appointment?.paymentMethod;

    if (isPending) {
      tempSections.push({
        name: 'Payment ',
        rows: [
          {
            type: 'NODE',
            label: '',
            value: [
              <Stack
                spacing={2.5}
                width="100%"
                alignItems="center"
                justifyContent="center">
                {pendingMethod && (
                  <Typography variant="body1" align="center">
                    {pendingMethod === 'CREDIT'
                      ? 'Checkout link sent. Waiting on payment.'
                      : pendingMethod === 'TERMINAL'
                      ? 'Charge sent to payment terminal. Waiting on payment.'
                      : 'Payment pending...'}
                  </Typography>
                )}
                <CircularProgress />
                {canCheckout && (
                  <Box
                    display="flex"
                    justifyContent="space-around"
                    alignItems="center"
                    gap={2}
                    width="100%">
                    <Button
                      variant="text"
                      className="text-btn"
                      onClick={onCancelCheckout}
                      disabled={isLoadingCancel}>
                      Cancel
                    </Button>
                  </Box>
                )}
              </Stack>,
            ],
          },
        ],
      });
    }

    const isComplete = paymentsGroup.payments.some(isPaymentComplete);
    if (isComplete) {
      tempSections.push({
        name: 'Details',
        rows: [
          {
            type: 'NODE',
            label: '',
            value: [
              <Stack spacing={1} width="100%">
                {getPaymentLineItems({
                  appointment: paymentsGroup?.appointment ?? null,
                  order: paymentsGroup?.order ?? null,
                  payments: paymentsGroup?.payments ?? [],
                  businessServices: getAllUserServices(user),
                  businessCashDiscountEnabled: isCashDiscountEnabled(
                    user?.business,
                  ),
                }).map(({ label, amount, isTotal }) => (
                  <PaymentLineItem
                    key={label}
                    label={label}
                    value={Math.abs(amount)}
                    isTotal={isTotal}
                    subtract={amount < 0 || isTotal}
                  />
                ))}
              </Stack>,
            ],
          },
        ],
      });
    }

    tempSections.push({
      // DO NOT CHANGE NAME without changing SideBarSection.tsx
      name: 'Checkout Transactions',
      rows: [],
      values: {
        paymentsGroup,
      },
    });

    const hasTransfers = paymentsGroup.payments.some(payment =>
      CREDIT_PAYMENT_METHODS.includes(payment.method),
    );
    if (hasTransfers)
      tempSections.push({
        // DO NOT CHANGE NAME without changing SideBarSection.tsx
        name: 'Checkout Transfer Details',
        rows: [],
        values: {
          paymentsGroup,
        },
      });

    const clientSection = getClientSection(paymentsGroup, {
      text: 'View',
      action: navigateToCustomer,
    });

    if (clientSection) tempSections.push(clientSection);

    return tempSections;
  }, [
    canCheckout,
    isLoadingCancel,
    navigateToCustomer,
    onCancelCheckout,
    paymentsGroup,
    user,
  ]);

  const isOpen = !!paymentId;

  return (
    <SideBarModal
      isLoading={!paymentsGroup}
      modalOpen={isOpen}
      setModalOpen={onClose}
      name="Checkout Detail"
      sections={sections}
    />
  );
}

export default PaymentsSideBarModal;
